<template>
  <div class="borrowerAttributeDateInput">
    <BorrowerAttributeConstructor
      v-if="combineInputs"
      :questionText="questionText"
      :description="description"
      :descriptionTooltip="descriptionTooltip"
      :tooltipText="tooltipText"
      :errorText="errorText"
      :helpText="helpText"
      :disclaimer="disclaimer">
      <template v-slot:input>
        <div class="form-floating">
          <input
            :id="'ba' + question.alias"
            :name="'ba' + question.alias"
            class="form-control"
            type="text"
            :class="{
              'is-invalid': v$ && v$.value.$invalid && v$.value.$dirty,
              'disabled': disabled,
              'disabled-repressed': disabled && repressDisabledStyle,
            }"
            :disabled="disabled"
            :placeholder="combinedDateLabel"
            :aria-label="label"
            :required="questionRequired"
            v-model="combinedDate"
            :maxlength="10"
            :minlength="10"
            max="9999"
            @focus="onFocus($event)"
            @blur="onBlur(v$, null, $event)"
            @keyup="onKeyup(v$, $event)"
            @change="onChange(v$, null, $event, false)"
            :data-cy="question.alias"
            :dreams-id="question.alias"
          />
          <label class="type-400" :for="'ba' + identifier"><span>{{ combinedDateLabel }}</span></label>
          <small v-if="mappedErrorMessages.combinedDate" class="invalid-feedback">
            <fa-icon :icon="['fal', 'circle-exclamation']"></fa-icon> {{ mappedErrorMessages.combinedDate }}
          </small>
        </div>
      </template>
    </BorrowerAttributeConstructor>
    <BorrowerAttributeConstructor
      v-else
      :questionText="questionText"
      :description="description"
      :descriptionTooltip="descriptionTooltip"
      :label="label"
      :tooltipText="tooltipText"
      :errorText="errorText"
      :helpText="helpText"
      :disclaimer="disclaimer"
    >
      <template v-slot:input>
        <div
          :class="{
            '@container d-flex flex-wrap': useContainerQueries,
            'row': !useContainerQueries
          }"
        >
          <div
            class="date-field"
            :class="{
              '@col-12 @col-xs-4 pb-3': useContainerQueries,
              'col': !useContainerQueries
            }"
          >
            <div class="form-floating">
              <select
                :id="'ba' + identifier"
                :name="'ba' + identifier"
                :data-cy="question.alias + '_month'"
                :dreams-id="question.alias + '_month'"
                class="form-select"
                v-model="month"
                @blur="onBlur(v$, v$.month)"
                @change="onChange(v$, v$.month, false)"
                :class="{
                  'label-visible': month || showLabel,
                  'is-invalid': (v$.month && v$.month.$invalid && v$.month.$dirty) || !isPastDate,
                  'no-value-select': !month && month !== 0,
                  'disabled': disabled,
                  'disabled-repressed': disabled && repressDisabledStyle,
                }"
                :disabled="disabled"
                :required="questionRequired"
                @click="setShowLabel(true)"
              >
                <option value="" selected disabled>Month</option>
                <option
                  v-for="(month, i) in months"
                  :value="i"
                  :key="i">
                  {{ displayMonthNum == 'inline' ? i + 1 : month.substring(0,3) }}
                </option>
              </select>
              <label class="type-400" v-if="month || showLabel" :for="'ba' + identifier">
                <span>Month</span>
              </label>
            </div>
          </div>
          <div
            class="date-field"
            :class="{
              '@col-12 @col-xs-4 pb-3': useContainerQueries,
              'col g-0': !useContainerQueries
            }"
          >
            <div class="form-floating">
              <input
                type="text"
                min="1"
                :max="daysMax"
                :id="'ba' + identifier + 'b'"
                :name="'ba' + identifier + 'b'"
                v-model="day"
                inputmode="numeric"
                @blur="onBlur(v$, v$.day)"
                @keyup="onChange(v$, v$.day, false)"
                @keydown.enter="onChange(v$.day, $event, true)"
                :class="{
                  'is-invalid': (v$.day && v$.day.$invalid && v$.day.$dirty) || !isPastDate,
                  'disabled': disabled,
                  'disabled-repressed': disabled && repressDisabledStyle,
                }"
                :disabled="disabled"
                :required="questionRequired"
                class="form-control"
                placeholder="Day"
                v-maska data-maska="##"
                :data-cy="question.alias + '_day'"
                :dreams-id="question.alias + '_day'"
              />
              <label class="type-400" :for="'ba' + identifier + 'b'">
                <span>Day</span>
              </label>
            </div>
          </div>
          <div
            class="date-field"
            :class="{
              '@col-12 @col-xs-4 pb-3': useContainerQueries,
              'col': !useContainerQueries
            }"
          >
            <div class="form-floating">
              <input
                type="text"
                :id="'ba' + identifier + 'c'"
                :name="'ba' + identifier + 'c'"
                :data-cy="question.alias + '_year'"
                :dreams-id="question.alias + '_year'"
                v-model="year"
                inputmode="numeric"
                @blur="onBlur(v$, v$.year)"
                @keyup="onChange(v$, v$.year, false)"
                @keydown.enter="onChange(v$.year, $event, true)"
                :class="{
                  'is-invalid': (v$.year && v$.year.$invalid && v$.year.$dirty) || !isPastDate,
                  'disabled': disabled,
                  'disabled-repressed': disabled && repressDisabledStyle,
                }"
                :disabled="disabled"
                :required="questionRequired"
                class="form-control"
                placeholder="Year"
                v-maska data-maska="####"/>
              <label class="type-400" :for="'ba' + identifier + 'b'">
                <span>Year</span>
              </label>
            </div>
          </div>
          <div v-if="mappedErrorMessages">
            <small v-for="key in Object.keys(mappedErrorMessages)" class="invalid-feedback d-block" :key="`${key}-error`">
              <fa-icon :icon="['fal', 'circle-exclamation']"></fa-icon> {{ mappedErrorMessages[key] }}
            </small>
          </div>
        </div>
      </template>
    </BorrowerAttributeConstructor>
  </div>
</template>

<script>
  import BorrowerAttributeConstructor from '~/components/borrowerAttributes/BorrowerAttributeConstructor.vue'
  import { getYear } from 'date-fns'
  import { format } from 'date-fns'
  import { isValid } from 'date-fns'
  import { toDate } from 'date-fns'
  import { isAfter } from 'date-fns'
  import { getDate } from 'date-fns'
  import { getDaysInMonth } from 'date-fns'
  import { getMonth } from 'date-fns'
  import { useVuelidate } from '@vuelidate/core'
  import { required, requiredIf, minLength, maxLength, between } from '@vuelidate/validators'

  const isValidYear = (value) => {
    const yearIsFourDigits = /^\d{4}$/.test(value)
    return yearIsFourDigits && value > 1900 && value <= getYear(new Date())
  }

  const isValidMonth = (value) => {
    return value > 0 && value <= 12
  }

  const isValidDay = (day, month) => {
    if (!isValidMonth(month)) {
      return false
    }
    const thirtyDayMonths = ['04','06','08','11']
    const thirtyOneDayMonths = ['01', '03', '05', '07', '09', '10', '12']
    const february = '02'

    switch (true) {
      case (thirtyDayMonths.includes(month)):
        return day > 0 && day <= 30
      case (thirtyOneDayMonths.includes(month)):
        return day > 0 && day <= 31
      case (february === month):
        // Not going to bother calculating if it's a leap year
        return day > 0 && day <= 29
    }
  }

  export default {
    props: {
      question: Object,
      disabled: Boolean,
      repressDisabledStyle: Boolean,
      v: Object,
      displayMonthNum: String,
      useContainerQueries: Boolean,
    },
    emits: ['invalidity','answer'],
    components: {
      BorrowerAttributeConstructor,
    },
    setup: () => ({ v$: useVuelidate() }),
    data () {
      return {
        format: 'yyyy-MM-dd',
        displayFormat: 'MM/dd/yyyy',
        months: [
          'January',
          'February',
          'March',
          'April',
          'May',
          'June',
          'July',
          'August',
          'September',
          'October',
          'November',
          'December'
        ],
        combinedDate: '',
        initialDate: '',
        year: '',
        day: '',
        month: '',
        showLabel: null,
      }
    },
    created: function () {
      this.$watch('invalid', (invalid) => {
        this.$emit('invalidity', invalid)
      }, {immediate: true})

      let separatedDate = this.question.value ? this.question.value.split('-') : ''
      this.initialDate = new Date(separatedDate[0], (separatedDate[1] - 1), separatedDate[2])
      let date = isValid(toDate(this.initialDate)) ? toDate(this.initialDate) : null
      if (date) {
        this.year = getYear(date)
        this.month = getMonth(date)
        this.day = getDate(date)
        this.combinedDate = this.combineInputs ? format(new Date(date), this.displayFormat) : ''
      }
      if (this.question.value) {
        this.$emit('answer', { value: this.question.value, isInvalid: false })
      }
    },
    validations: function () {
      if (!this.combineInputs) {
        return {
          month: {
            required: requiredIf(() => !this.question.mustAllowNull),
          },
          day: {
            required: requiredIf(() => !this.question.mustAllowNull),
            minLength: minLength('1'),
            maxLength: maxLength('2'),
            between: between(1, this.daysMax),
          },
          year: {
            required: requiredIf(() => !this.question.mustAllowNull),
            minLength: minLength('4'),
            maxLength: maxLength('4'),
            isValidYear
          }
        }
      } else {
        return {
          combinedDate: {
            required: requiredIf(() => !this.question.mustAllowNull),
            minLength: minLength(10),
            maxLength: maxLength(10),
            pattern: (val) => {
              if (!this.isPastDate) {
                return false
              }
              const [yearValid, monthValid, dayValid] = this.isValidDate(val)
              if (this.combinedDate.length === 10) {
                return dayValid && monthValid && yearValid
              } else if (this.combinedDate >= 5 && this.combinedDate < 10) {
                return dayValid && monthValid
              } else {
                return monthValid
              }
            }
          }
        }
      }
    },
    computed: {
      identifier() {
        return this.question.id || this.question.alias
      },
      invalid() {
        return this.v$.$invalid
      },
      combineInputs() {
        // Disabling at request of UX designer. Is functional and has the animation of the label
        // moving from the placeholder into the border.
        return this.question.type == 'date-single'
        // return false
      },
      combinedDateLabel() {
        return this.question.name || ''
      },
      daysMax () {
        if (this.year.length === 4 && this.month >= 0) {
          return getDaysInMonth(new Date(this.year, this.month))
        } else {
          switch (this.month) {
            case 1:
              return 29
              break
            case 3 || 5 || 8 || 10:
              return 30
              break
            default:
              return 31
          }
        }
      },
      label() {
        return this.question.name || ''
      },
      tooltipText() {
        return this.question.tooltip || this.question.helpText || ''
      },
      questionRequired() {
        return this.question.required || !this.question.mustAllowNull
      },
      questionText() {
        return this.question.questionText || ''
      },
      description() {
        return this.question.description || ''
      },
      descriptionTooltip() {
        return this.question.descriptionTooltip || ''
      },
      helpText() {
        return this.question.tooltip ? this.question.helpText : ''
      },
      errorText() {
        return !Object.keys(this.mappedErrorMessages).length && !this.isPastDate && !this.combineInputs ? 'Please input a valid past date' : ''
      },
      disclaimer() {
        return this.question.disclaimer || ''
      },
      mappedErrorMessages() {
        const messages = {}
        if (this.combineInputs && this.v$.combinedDate?.$error && !this.combinedDate) {
          messages.combinedDate = 'Required'
        }

        if (this.combineInputs && this.v$.combinedDate?.$error && this.combinedDate) {
          messages.combinedDate = 'Please enter a valid date'
        }

        if (!this.combineInputs && this.v$.month?.$error) {
          messages.month = 'Please select a month'
        }

        if (!this.combineInputs && this.v$.day?.$error) {
          messages.day = 'Please enter a valid calendar day'
        }

        if (!this.combineInputs && this.v$.year?.$error && this.isPastDate) {
          messages.year = 'Please input a valid year'
        }
        return messages
      },
      isPastDate () {
        const newValue = this.combineInputs ? new Date(`${this.combinedDate.replace('/', '-')}T00:00-0800`) : new Date(this.year, this.month, this.day)
        return !isAfter(newValue, new Date())
      },
    },
    methods: {
      updateDate () {
        let newValue
        if (!this.combineInputs) {
          const yearInt = parseInt(this.year)
          if (isValidYear(yearInt) && (this.month !== '') && this.day) {
            newValue = new Date(this.year, this.month, this.day)
            if (isValid(newValue)) {
              return format(newValue, this.format)
            } else {
              return null
            }
          }
        } else {
          if (this.isValidDate(this.combinedDate)) {
            // without the T00:00-0800 it will interpret the date incorrectly and will likely offset the date by one day
            newValue = new Date(`${this.combinedDate.replace('/', '-')}T00:00-0800`)
            if (isValid(newValue) && this.isPastDate) {
              return format(newValue, this.format)
            } else {
              return null
            }
          }
        }
      },
      onBlur(v$, v$ChangedField = null, e) {
        if (!this.combineInputs) {
          v$.month.$dirty && v$.month.$invalid && this.showLabel && this.setShowLabel(false)
          v$ChangedField.$touch() // Mark the field as dirty to display an error
        } else {
          if (e.target.value && !this.v$.$error) {
            e.target.type = 'text'
            const [year, month, day] = e.target.value.split('-')
            this.combinedDate = format(new Date(year, month - 1, day), this.displayFormat)
            e.target.value = this.combinedDate
            this.combinedDateClicked = false
          }

          v$.$touch()
        }
      },
      onChange (v$, v$ChangedField, e, submitImmediately) {
        if (this.combineInputs && this.combinedDate) {
          this.month = this.combinedDate.split('/')[0]
          this.day = this.combinedDate.split('/')[1]
          this.year = this.combinedDate.split('/')[2]
        }
        const date = this.updateDate()
        this.$emit('answer', { value: date, isInvalid: v$.$invalid || !this.isPastDate, submitImmediately })
        this.combineInputs && v$.$touch()
      },
      onKeyup(v$, e) {
        if (this.combineInputs && this.combinedDate && this.combinedDate.length === 10) {
          v$.$touch()
        }
      },
      onFocus(e) {
        /* 12/31/2021 - swajalisduf
          This makes the floating label animation with the date input possible. It essentially sets it as a text input
          when empty so that when the user clicks on it you get the animation. But you also get the benefits of using
          the date type on the input.

          This change has to happen in a focus event. For some reason focus isn't properly set even with .focus() on
          the input if you do this in a click event while changing the input type
        */
        if(this.combineInputs && e.target.type === 'text') {
          // e.target.type = 'date'
          e.target.value = this.combinedDate ? format(new Date(this.combinedDate), this.format) : ''
        }
      },
      setShowLabel (payload) {
        this.showLabel = payload
      },
      isValidDate(value) {
        let dayValid = false
        let monthValid = false
        let yearValid = false
        if (this.combinedDate) {
          const dateVal = format(new Date(value), this.displayFormat)
          const [month, day, year] = dateVal.split('/')

          yearValid = year && isValidYear(year)
          monthValid = month && isValidMonth(month)
          dayValid =  day && isValidDay(day, month)
        }


        return [yearValid, monthValid, dayValid]
      }
    }
  }
</script>

<style lang="scss" scoped>
@include container-breakpoint-up(xs) {
  // Alternative syntax: @container (min-width: #{$xs-container-breakpoint}) {
  .date-field {
    padding-bottom: 0rem !important; // override pb (bootstrap)
    padding-right: 0.75rem;
  }
}

</style>
